﻿import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Modal,
    Toggle,
    Sticky,
    IColumn,
    Spinner,
    Dropdown,
    Checkbox,
    TextField,
    IconButton,
    DetailsRow,
    SpinnerSize,
    TooltipHost,
    DetailsList,
    SelectionMode,
    DetailsHeader,
    PrimaryButton,
    DefaultButton,
    DirectionalHint,
    IDropdownOption,
    IDetailsRowProps,
    StickyPositionType,
    IDetailsHeaderProps,
    DetailsListLayoutMode
} from "@fluentui/react";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import styles from "./CampaignManager.module.css";
import { gfile, gloggedinuser, gselectedgroup, gcampaign, gcampaigntarget, gcampaignconfig, gcampaignscheduleentry } from "../../interfaces";
import {
    qAddCampaign,
    qDelCampaign,
    qGetCampaigns,
    qSetDefCampaign,
    qScheduleCampaign,
    qRescheduleTarget,
    qAddCampaignTargets,
    qRemoveCampaignTarget,
    qUpdateCampaignTarget,
    qGetAllCampaignTargets,
    qSendCampaignInvites,
    qAiMatchPosition
} from "../../api/qpi";

import CampaignChatMembers from "../CampaignChatMembers/CampaignChatMembers";

import {campaignTextFieldStyles, tooltipStyles, toggleStylesNewCampaignEnable} from "../../styles_glob";
import { trimLow } from "../../util_glob";
import { stateColors, cmDropdownStyles, cmTextFieldCompactStyles, cmPrimaryButtonStyles, cmDefaultButtonStyles, cmSmallPrimaryStyles, detailsListCampaignStyles, orderHeaderCampaignCustomStyles } from "./CampaignManagerStyles";
import {DateRangePicker} from '../DateRangePicker/DateRangePicker';
import { getActiveCampaignId, get_campaigns_for_selected_group } from "../../pages/chat/ChatUtil";
import { toIsoDateStr, getDateRange } from "../../util_date_glob";

// ദ്ദി(˵ •̀ ᴗ - ˵ ) ✧

interface CampaignManagerProps {
    selectedGroup: gselectedgroup;
    loggedInUser: gloggedinuser;
    onGroupUpdate?: (patch: Partial<gselectedgroup>) => void;
    onInviteMember?: (usermail: string) => Promise<Response>;
    fileList: gfile[];
}

const CampaignManager: React.FC<CampaignManagerProps> = ({ selectedGroup, loggedInUser, onGroupUpdate, onInviteMember, fileList }) => {
    const [campaigns, setCampaigns]             = useState<gcampaign[]>([]);
    const [selectedCampaignId, setSelectedCampaignId] = useState<string>(() => getActiveCampaignId(selectedGroup, get_campaigns_for_selected_group(selectedGroup)));
    const [allTargets, setAllTargets]           = useState<gcampaigntarget[]>([]);
    const [loading, setLoading]                 = useState(false);
    const [loadingTargets, setLoadingTargets]   = useState(false);

    // new campaign modal
    const [showNewModal, setShowNewModal]       = useState(false);
    const [newName, setNewName]                 = useState('');
    const [newDesc, setNewDesc]                 = useState('');
    const [newStartDate, setNewStartDate]       = useState('');
    const [newEndDate, setNewEndDate]           = useState('');
    const [isEditing, setIsEditing]             = useState(false);
    const [isInactive, setIsInactive]           = useState(0);
    const [newType, setNewType]                 = useState<'phone' | 'chat'>('phone');
    const [membersRefreshKey, setMembersRefreshKey] = useState(0);   // bump to force chat member list re-fetch

    // manual add contact
    const [addName, setAddName]   = useState('');
    const [addPhone, setAddPhone] = useState('');
    const [addEmail, setAddEmail] = useState('');
    const [addPosition, setAddPosition] = useState('');

    // inline editing
    const [editingTargetId, setEditingTargetId] = useState<string | null>(null);
    const [editName, setEditName]   = useState('');
    const [editPhone, setEditPhone] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [editPosition, setEditPosition] = useState('');
    const [editCampaignIds, setEditCampaignIds] = useState<string[]>(['root']);
    const [editSchedDate, setEditSchedDate]     = useState<string>('');     // iso date for schedule reschedule
    const origSchedDate = useRef<string>('');                               // track original to detect changes

    const csvRef = useRef<HTMLInputElement>(null);

    // ───── campaign id → display name map ─────
    const campaignNameMap: Record<string, string> = {};
    campaigns.forEach(c => { campaignNameMap[c.campaign_id] = c.campaign_display_name; });

    // ───── load campaigns ─────
    const loadCampaigns = useCallback(async () => {
        setLoading(true);
        try {
            const data: gcampaign[] = await qGetCampaigns(loggedInUser, selectedGroup.selectionId);

            import.meta.env.DEV && console.log("Loaded campaigns:", data);

            setCampaigns(data);
        } catch { toast.error("Failed to load campaigns"); }
        setLoading(false);
    }, [loggedInUser, selectedGroup.selectionId]);

    useEffect(() => { loadCampaigns() }, [loadCampaigns]);

    // ───── load all contacts for the space ─────
    const loadAllTargets = useCallback(async () => {
        setLoadingTargets(true);
        try {
            const data: gcampaigntarget[] = await qGetAllCampaignTargets(loggedInUser, selectedGroup.selectionId);
            setAllTargets(data);
        } catch { toast.error("Failed to load contacts"); }
        setLoadingTargets(false);
    }, [loggedInUser, selectedGroup.selectionId]);

    useEffect(() => { loadAllTargets() }, [loadAllTargets]);

    // ───── filtered view ─────
    const displayedTargets = selectedCampaignId
        ? selectedCampaignId === 'root'
            ? allTargets // "All Contacts" view shows everything, including contacts in campaigns (since they should still be visible in the root view)
            : allTargets.filter(t => t.active_campaign_ids?.includes(selectedCampaignId))
        : allTargets


    // ───── campaign dropdown options ─────
    const campaignOptions: IDropdownOption[] = [
        { key: 'root', text: 'All Contacts' },
        ...campaigns.map(c => ({ key: c.campaign_id, text: c.campaign_display_name }))
    ];

    const campaignTypeOptions: IDropdownOption[] = [
        { key: 'phone', text: 'Phone — outbound calls' },
        { key: 'chat', text: 'Chat — email invites' },
    ];
    const onRenderDetailsHeaderV2 = (props: IDetailsHeaderProps, defaultRender?: (props: IDetailsHeaderProps) => JSX.Element) => {
        return (
            <Sticky stickyPosition={StickyPositionType.Header} isScrollSynced={true}>
                <DetailsHeader
                    {...props}
                    styles={orderHeaderCampaignCustomStyles}
                />
            </Sticky>
        );
    };

    const selectedCampaign = campaigns.find(c => c.campaign_id === selectedCampaignId);
    const isChatCampaign = selectedCampaign?.campaign_config?.type === 'chat';

    if (selectedCampaign && selectedCampaign?.inactive) {
        setIsInactive(1);
    }

    // ───── new / edit campaign ─────
    const openNewModal = (edit?: boolean) => {
        if (edit && selectedCampaign) {
            setNewName(selectedCampaign.campaign_display_name || '');
            setNewDesc(selectedCampaign.campaign_config?.description || '');
            setNewStartDate(selectedCampaign.campaign_start_date || '');
            setNewEndDate(selectedCampaign.campaign_end_date || '');
            setNewType((selectedCampaign.campaign_config?.type as 'phone' | 'chat') || 'phone');
            setIsEditing(true);
        } else {
            setNewName(''); setNewDesc(''); setNewStartDate(''); setNewEndDate('');
            setNewType('phone');
            setIsEditing(false);
        }
        setShowNewModal(true);
    };

    const saveCampaign = async () => {
        if (!newName.trim()) { toast.warning('Name is required'); return; }
        const typeForCfg: 'phone' | 'chat' = isEditing ? ((selectedCampaign?.campaign_config?.type as 'phone' | 'chat') || 'phone') : newType;
        const config: gcampaignconfig = {...(newDesc ? { description: newDesc.trim() } : {}), type: typeForCfg};
        try {
            const res = await qAddCampaign(loggedInUser, selectedGroup.selectionId, config, isEditing ? selectedCampaignId : undefined, newStartDate || undefined, newEndDate || undefined, newName.trim());
            toast.success(isEditing ? 'Campaign updated' : 'Campaign created', { autoClose: 100 });
            setShowNewModal(false);
            await loadCampaigns();
            if (!isEditing && res.campaign_id != '') setSelectedCampaignId(res.campaign_id);
        } catch { toast.error('Failed to save campaign'); }
    };

    const saveCampaignV2 = async (
        suplyoptlocal?:boolean,
        camp_name?:string,
        desc?:string,
        startDate?:string,
        endDate?:string,
        isInactiveFlag?: number,
        campaign_id?: string
    ) => {
        if (suplyoptlocal && suplyoptlocal === true) {
        } else {
            if (!newName.trim()) { toast.warning('Name is required'); return; }
        }

        try {
            if (suplyoptlocal && suplyoptlocal === true) {
                camp_name = camp_name || newName;
                desc = desc || newDesc;
                startDate = startDate || newStartDate;
                endDate = endDate || newEndDate;
                isInactiveFlag = isInactiveFlag !== undefined ? isInactiveFlag : isInactive;
                const existingType: 'phone' | 'chat' = (campaigns.find(c => c.campaign_id === campaign_id)?.campaign_config?.type as 'phone' | 'chat') || 'phone';
                const config: gcampaignconfig = {...(desc ? { description: desc.trim() } : {}), type: existingType};

                const res = await qAddCampaign(
                    loggedInUser,
                    selectedGroup.selectionId,
                    config,
                    campaign_id,
                    startDate || undefined,
                    endDate || undefined,
                    camp_name.trim(),
                    isInactiveFlag ? 1 : 0
                );

                toast.success('Campaign created', { autoClose: 400 });
                setShowNewModal(false);
                await loadCampaigns();
                if (!isEditing && res.campaign_id != '') setSelectedCampaignId(res.campaign_id);
            } else {
                const typeForCfg: 'phone' | 'chat' = isEditing ? ((selectedCampaign?.campaign_config?.type as 'phone' | 'chat') || 'phone') : newType;
                const config: gcampaignconfig = {...(newDesc ? { description: newDesc.trim() } : {}), type: typeForCfg};
                const res = await qAddCampaign(loggedInUser, selectedGroup.selectionId, config, isEditing ? selectedCampaignId : undefined, newStartDate || undefined, newEndDate || undefined, newName.trim(), isInactive ? 1 : 0);
                toast.success(isEditing ? 'Campaign updated' : 'Campaign created', { autoClose: 400 });
                setShowNewModal(false);
                await loadCampaigns();
                if (!isEditing && res.campaign_id != '') setSelectedCampaignId(res.campaign_id);
            }
        } catch {
            toast.error('Failed to save campaign');
        }
        return
    };


    const deleteCampaign = async () => {
        if (!selectedCampaignId) return;
        if (!window.confirm(`Delete campaign "${selectedCampaign?.campaign_display_name || selectedCampaignId}"? This removes all progress data.`)) return;
        try {
            await qDelCampaign(loggedInUser, selectedCampaignId);
            toast.success('Campaign deleted');
            setSelectedCampaignId('');
            await loadCampaigns();
            await loadAllTargets();
        } catch { toast.error('Failed to delete campaign'); }
    };

    // ───── send chat-campaign mail invites ─────
    const sendCampaignInvites = async () => {
        if (!selectedCampaignId || selectedCampaignId === 'root') return;
        try {
            const res = await qSendCampaignInvites(loggedInUser, selectedCampaignId, selectedGroup.selectionId);
            toast.success(`Sent ${res.sent ?? 0} invite(s). Skipped: ${res.skipped ?? 0}.`);
        } catch { toast.error('Failed to send mail invites'); }
    };

    // ───── AI: match each target's position to a suitable 'data' document ─────
    // For chat-invite campaigns only. Returns map of target_id -> target_campaign_fids array
    // ([{ "<campaign_id>": ["<fileid>"] }]). Targets with no position are skipped.
    const matchPositionsToFids = async (
        targetCampaign: string,
        targetsWithPos: { target_id: string; position: string }[]
    ): Promise<Record<string, Record<string, string[]>[]>> => {
        const out: Record<string, Record<string, string[]>[]> = {};


        import.meta.env.DEV && console.log('‹file_list›', fileList)

        if (!isChatCampaign || !targetCampaign || targetCampaign === 'root') return out;
        import.meta.env.DEV && console.log('‹hop›')

        const dataFiles = (fileList || [])
            .filter(f => f.doc_category === 'data')
            .map(f => ({ fileid: f.fileid, name: f.name }));


        import.meta.env.DEV && console.log('‹data_files›', dataFiles)

        if (dataFiles.length === 0) return out;

        import.meta.env.DEV && console.log('‹hop_2›')
        const positioned = targetsWithPos.filter(t => t.position && t.position.trim());

        import.meta.env.DEV && console.log('‹position›', positioned)

        if (positioned.length === 0) return out;

        try {
            const matches = await qAiMatchPosition(loggedInUser, selectedGroup.selectionId, dataFiles, positioned);
            import.meta.env.DEV && console.log('‹matches›', matches)
            for (const m of matches) {
                if (m?.target_id && m?.fileid) {
                    out[m.target_id] = [{ [targetCampaign]: [m.fileid] }];
                }
            }
            import.meta.env.DEV && console.log('‹out›', out)
        } catch {
            import.meta.env.DEV && console.log("‹errr_ai_match_pos›");
            toast.warning('Could not match documents to positions');
        }
        return out;
    };

    // ───── add single contact ─────
    const addManualContact = async (targetCampaign:string) => {
        if (!isChatCampaign && !addPhone.trim() && selectedCampaignId != 'root') { toast.warning('Phone number is required'); return; }
        const email = trimLow(addEmail);
        // chat campaigns: the member needs web access, so register them in the space permission system first
        if (isChatCampaign) {
            if (!email) { toast.warning('Email is required to add a chat campaign member'); return; }
            if (onInviteMember) {
                try {
                    const res = await onInviteMember(email);
                    if (!res || !res.ok) { toast.error('Failed to add member to the space'); return; }
                } catch { toast.error('Failed to add member to the space'); return; }
            }
        }
        try {
            const targetId = email || uuidv4(); // use email as ID if available, otherwise generate UUID
            import.meta.env.DEV && console.log('‹prea›', [{ target_id: targetId, position: addPosition }])
            const fidsMap  = await matchPositionsToFids(targetCampaign || 'root', [{ target_id: targetId, position: addPosition }]);
            const res = await qAddCampaignTargets(
                loggedInUser,
                targetCampaign || 'root',
                [{
                    target_id: targetId,
                    target_name: addName.trim(),
                    target_phone_number: addPhone.trim(),
                    target_email: email,
                    target_position: addPosition.trim(),
                    ...(fidsMap[targetId] ? { target_campaign_fids: fidsMap[targetId] } : {}),
                    target_meta: {}
                }]);
            toast.success(isChatCampaign ? 'Member added' : 'Contact added');
            setAddName(''); setAddPhone(''); setAddEmail(''); setAddPosition('');
            await loadAllTargets();
            if (targetCampaign) await loadCampaigns();
            setMembersRefreshKey(k => k + 1);
        } catch {
            import.meta.env.DEV && console.log("‹errr_match_pos_fids›");
            toast.error('Failed to add contact');
        }
    };

    // ───── cvs upload ─────
    const handleCSV = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const targetCampaign = selectedCampaignId || 'root';

        if (!targetCampaign) { toast.warning('Create a campaign first'); e.target.value = ''; return; }

        const reader    = new FileReader();
        reader.onload   = async (ev) => {
            const text  = ev.target?.result as string;
            const lines = text.split(/\r?\n/).filter(l => l.trim());
            if (lines.length < 2) { toast.warning('CSV must have a header row and at least one data row'); return; }
            const headers  = lines[0].split(',').map(h => h.trim().toLowerCase());
            const nameIdx  = headers.findIndex(h => h.includes('name'));
            const phoneIdx = headers.findIndex(h => h.includes('phone'));
            const emailIdx = headers.findIndex(h => h.includes('email'));
            const posIdx   = headers.findIndex(h => h.includes('position') || h.includes('role'));
            // phone is required for phone campaigns; chat-invite campaigns can rely on email instead
            if (phoneIdx < 0 && !isChatCampaign) { toast.error("CSV must have a column containing 'phone'"); return; }

            const rows = lines.slice(1).map(line => {
                const cols = line.split(',').map(c => c.trim());
                return {
                    target_id: emailIdx    >= 0 ? trimLow(cols[emailIdx]) : uuidv4(), // use email as ID if available, otherwise generate UUID
                    target_name: nameIdx   >= 0 ? cols[nameIdx] || '' : '',
                    target_phone_number: phoneIdx >= 0 ? cols[phoneIdx] || '' : '',
                    target_email: emailIdx >= 0 ? trimLow(cols[emailIdx] || '') : '',
                    target_position: posIdx >= 0 ? cols[posIdx] || '' : ''
                };
            }).filter(r => isChatCampaign ? (r.target_phone_number || r.target_email) : r.target_phone_number);

            if (rows.length === 0) { toast.warning('No valid rows found'); return; }
            try {
                // chat campaigns: register each member (with an email) in the space permission system first
                if (isChatCampaign && onInviteMember) {
                    for (const r of rows) {
                        const em = (r.target_email || '').trim();
                        if (em) { try { await onInviteMember(em); } catch { /* skip individual failures */ } }
                    }
                }
                // chat campaigns: AI-match each position to a suitable 'data' document (single batched call)
                const fidsMap = await matchPositionsToFids(
                    targetCampaign,
                    rows.map(r => ({ target_id: r.target_id, position: r.target_position }))
                );
                const rowsWithFids = rows.map(r => fidsMap[r.target_id] ? { ...r, target_campaign_fids: fidsMap[r.target_id] } : r);
                await qAddCampaignTargets( loggedInUser, targetCampaign, rowsWithFids );
                toast.success(`${rows.length} contact(s) imported`)
                await loadAllTargets();
                await loadCampaigns();
                setMembersRefreshKey(k => k + 1);
            } catch { toast.error('Failed to add contacts from CSV') }
        };
        reader.readAsText(file);
        e.target.value = '';
    };

    // ───── local state helpers ─────
    const updateTargetLocal = (targetId: string, updater: (t: gcampaigntarget) => gcampaigntarget) => {
        setAllTargets(prev => prev.map(t => t.target_id === targetId ? updater(t) : t));
    };
    const removeTargetLocal = (targetId: string) => {
        setAllTargets(prev => prev.filter(t => t.target_id !== targetId));
    };

    // ───── inline editing ─────
    const startEdit = (target: gcampaigntarget) => {
        setEditingTargetId(target.target_id);
        setEditName(target.target_name || '');
        setEditPhone(target.target_phone_number || '');
        setEditEmail(target.target_email || '');
        setEditPosition(target.target_position || '');
        setEditCampaignIds([...(target.active_campaign_ids || ['root'])]);
        // populate schedule date from latest entry for selected campaign
        const schedArr = selectedCampaignId ? target.schedules?.[selectedCampaignId] : undefined;
        const latest = schedArr?.length ? schedArr[schedArr.length - 1] : undefined;
        const isoDate = latest?.scheduled_time ? latest.scheduled_time.split('T')[0] : '';
        setEditSchedDate(isoDate);
        origSchedDate.current = isoDate;
    };

    const cancelEdit = () => {
        setEditingTargetId(null);
        setEditName(''); setEditPhone(''); setEditEmail(''); setEditPosition('');
        setEditCampaignIds([]);
        setEditSchedDate(''); origSchedDate.current = '';
    };

    const saveEdit = async () => {
        if (!editingTargetId) return;
        if (!isChatCampaign && !editPhone.trim() && selectedCampaignId != 'root') { toast.warning('Phone number is required'); return; }
        const original = allTargets.find(t => t.target_id === editingTargetId);
        if (!original) return;
        const origIds = original.active_campaign_ids || ['root'];
        const added   = editCampaignIds.filter(id => !origIds.includes(id));
        const removed = origIds.filter(id => !editCampaignIds.includes(id));
        try {
            await qUpdateCampaignTarget(
                loggedInUser,
                editingTargetId,
                editName.trim(),
                editPhone.trim(),
                editEmail.trim(),
                editPosition.trim()
            );

            for (const cid of added) await qAddCampaignTargets(
                loggedInUser,
                cid,
                [{
                    target_id: editingTargetId,
                    target_name: editName.trim(),
                    target_phone_number: editPhone.trim(),
                    target_email: trimLow(editEmail),
                    target_position: editPosition.trim(),
                    target_meta: {}
                }]
            );

            for (const cid of removed) await qRemoveCampaignTarget(loggedInUser, cid, editingTargetId);

            // ── reschedule if date changed ──
            let newSchedTime: string | undefined;
            if (selectedCampaignId && editSchedDate && editSchedDate !== origSchedDate.current) {
                try {
                    const res = await qRescheduleTarget(
                        loggedInUser,
                        editingTargetId,
                        selectedCampaignId,
                        selectedGroup.selectionId,
                        selectedGroup.companyid || '',
                        editSchedDate,
                    );
                    newSchedTime = res.scheduled_time;
                } catch { toast.error("Failed to reschedule"); }
            }

            updateTargetLocal(
                editingTargetId, t => {
                    const updated = {
                        ...t,
                        target_id: editingTargetId,
                        target_name: editName.trim(),
                        target_phone_number: editPhone.trim(),
                        target_email: trimLow(editEmail),
                        target_position: editPosition.trim(),
                        active_campaign_ids: [...editCampaignIds]
                    } as gcampaigntarget;
                    // patch local schedule with new time if reschedule succeeded
                    if (newSchedTime && selectedCampaignId && updated.schedules?.[selectedCampaignId]) {
                        const arr = [...updated.schedules[selectedCampaignId]];
                        if (arr.length) {
                            arr[arr.length - 1] = { ...arr[arr.length - 1], scheduled_time: newSchedTime, state: 'scheduled' };
                            updated.schedules = { ...updated.schedules, [selectedCampaignId]: arr };
                        }
                    }
                    return updated;
                });
            toast.success("Contact updated", { autoClose: 50 });
            if (added.length || removed.length) await loadCampaigns();
            setMembersRefreshKey(k => k + 1);
            cancelEdit();
        } catch { toast.error("Failed to update contact"); }
    };

    const toggleEditCampaign = (campId: string) => {
        setEditCampaignIds(prev => prev.includes(campId) ? prev.filter(id => id !== campId) : [...prev, campId]);
    };

    // ───── remove target from campaign ─────
    const removeTarget = async (target: gcampaigntarget) => {
        const campId = selectedCampaignId || 'root';
        if (!campId) return;
        try {
            await qRemoveCampaignTarget(loggedInUser, campId, target.target_id);
            const newIds = (target.active_campaign_ids || ['root']).filter(id => id !== campId);
            if (newIds.length === 0) { removeTargetLocal(target.target_id); }
            else { updateTargetLocal(target.target_id, t => ({ ...t, active_campaign_ids: newIds })); }
            toast.success("Removed from campaign", { autoClose: 200 });
            await loadCampaigns();
            setMembersRefreshKey(k => k + 1);
        } catch { toast.error("Failed to remove contact"); }
    };

    // ───── columns ─────
    const columns = [
        { key: "name", name: "Name", fieldName: "target_name", maxWidth: 200,  isResizable: true, targetWidthProportion: 1,
            onRender: (item: gcampaigntarget) => editingTargetId === item.target_id
                ? <TextField value={editName} onChange={(_, v) => setEditName(v || '')} styles={cmTextFieldCompactStyles} className={styles.editField} />
                : <span className={styles.editFieldSpan}>{item.target_name}</span>
        },
        { key: "phone", name: "Phone", fieldName: "target_phone_number", maxWidth: 200, isResizable: true, targetWidthProportion: 1,
            onRender: (item: gcampaigntarget) => editingTargetId === item.target_id
                ? <TextField value={editPhone} onChange={(_, v) => setEditPhone(v || '')} styles={cmTextFieldCompactStyles} className={styles.editField} />
                : <span className={styles.editFieldSpan}>{item.target_phone_number}</span>
        },
        { key: "email", name: "Email", fieldName: "target_email", maxWidth: 200,  isResizable: true, targetWidthProportion: 1,
            onRender: (item: gcampaigntarget) => editingTargetId === item.target_id
                ? <TextField value={editEmail} onChange={(_, v) => setEditEmail(v || '')} styles={cmTextFieldCompactStyles} className={styles.editField} />
                : <span className={styles.editFieldSpan}>{item.target_email}</span>
        },
        { key: "position", name: "Position", fieldName: "target_position", maxWidth: 200, isResizable: true, targetWidthProportion: 1,
            onRender: (item: gcampaigntarget) => editingTargetId === item.target_id
                ? <TextField value={editPosition} onChange={(_, v) => setEditPosition(v || '')} styles={cmTextFieldCompactStyles} className={styles.editField} />
                : <span className={styles.editFieldSpan}>{item.target_position}</span>
        },
        { key: "campaigns", name: "Campaigns", fieldName: "", maxWidth: 1000, isResizable: true, targetWidthProportion: 1, ...(selectedCampaignId === 'root' ? { minWidth: 100} : { }),
            onRender: (item: gcampaigntarget) => editingTargetId === item.target_id ? (
                <div className={styles.tagRow}>
                    {campaigns.map(c => {
                        const active = editCampaignIds.includes(c.campaign_id);
                        return (
                            <span key={c.campaign_id} className={`${styles.tag} ${active ? styles.tagActive : styles.tagInactive}`} onClick={() => toggleEditCampaign(c.campaign_id)} title={active ? 'Click to remove' : 'Click to add'}>
                                {active ? '✕ ' : '+ '}{c.campaign_display_name}
                            </span>
                        );
                    })}
                </div>
            ) : (
                <div className={styles.tagRow}>
                    {(item.active_campaign_ids || ['root']).map(cid => (
                        <span key={cid} className={`${styles.tag} ${cid === selectedCampaignId ? styles.tagActive : ''}`}>
                            {campaignNameMap[cid] || cid}
                        </span>
                    ))}
                </div>
            )
        },
        ...( selectedCampaignId == 'root' ? [] :
            [{ key: "schedule", name: "Schedule", fieldName: "", maxWidth: 1800, minWidth: 50, isResizable: true, isPadded: false, targetWidthProportion: 5,
                onRender: (item: gcampaigntarget) => {
                    const stateLabel = (s: string) => s.replace(/_/g, ' ');
                    const formatTime = (iso?: string) => {
                        if (!iso) return '';
                        try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',  hour12: false }); } catch { return ''; }
                    };

                    const TERMINAL = new Set(['complete', 'cancelled', 'campaign_ended', 'escalate']);

                    // ── stack consecutive same-state entries into groups ──
                    type StackedNode = { state: string; entries: gcampaignscheduleentry[]; latest: gcampaignscheduleentry };

                    const stackEvents = (arr: gcampaignscheduleentry[]): StackedNode[] => {
                        const nodes: StackedNode[] = [];
                        for (const entry of arr) {
                            const prev = nodes.length ? nodes[nodes.length - 1] : null;
                            if (prev && prev.state === entry.state) {
                                prev.entries.push(entry);
                                prev.latest = entry; // latest is last in array
                            } else {
                                nodes.push({ state: entry.state, entries: [entry], latest: entry });
                            }
                        }
                        return nodes;
                    };

                    // ── render stacked hover card (latest on top) ──
                    const renderStackedCard = (node: StackedNode) => {
                        const color = stateColors[node.state] || '#666';
                        const reversed = [...node.entries].reverse(); // latest first
                        return (
                            <div className={styles.callCard} style={{ maxHeight: 460, overflowY: 'auto' }}>
                                <div className={styles.callCardHead}>
                                    <div className={styles.callCardIcon} style={{ background: color }}>
                                        {node.entries.length > 1 ? node.entries.length : '📞'}
                                    </div>
                                    <div className={styles.callCardHeadText}>
                                        <span className={styles.callCardHeadState} style={{ color }}>
                                            {stateLabel(node.state)}{node.entries.length > 1 ? ` × ${node.entries.length}` : ''}
                                        </span>
                                        {node.latest.updated_at && <span className={styles.callCardHeadTime}>{formatTime(node.latest.updated_at)}</span>}
                                    </div>
                                </div>
                                {reversed.map((entry, ri) => {
                                    const convoCollected = entry.convo_id ? item.convo_data?.[String(entry.convo_id)] : undefined;
                                    const hasCollected = convoCollected && Object.keys(convoCollected).length > 0;
                                    const isFirst = ri === 0;
                                    return (
                                        <div key={ri} className={styles.stackBlock} style={!isFirst ? { opacity: 0.85 - ri * 0.08 } : undefined}>
                                            {ri > 0 && <div className={styles.stackBlockDivider} />}
                                            <div className={styles.stackBlockHeader}>
                                                <span className={styles.stackBlockIndex} style={{ background: color }}>
                                                    {isFirst ? 'Latest' : `#${node.entries.length - ri}`}
                                                </span>
                                                <span className={styles.stackBlockTime}>{formatTime(entry.updated_at || entry.scheduled_time)}</span>
                                            </div>
                                            <div className={styles.callCardMeta}>
                                                {entry.convo_id && (
                                                    <div className={styles.callCardMetaRow}>
                                                        <span className={styles.callCardMetaLabel}>Convo</span>
                                                        <span className={styles.callCardMetaVal} style={{ fontFamily: 'monospace', fontSize: 10 }}>{entry.convo_id}</span>
                                                    </div>
                                                )}
                                                {entry.job_id && (
                                                    <div className={styles.callCardMetaRow}>
                                                        <span className={styles.callCardMetaLabel}>Job</span>
                                                        <span className={styles.callCardMetaVal} style={{ fontFamily: 'monospace', fontSize: 10 }}>{entry.job_id.slice(0, 8)}…</span>
                                                    </div>
                                                )}
                                                {entry.delay_info && (
                                                    <div className={styles.callCardMetaRow}>
                                                        <span className={styles.callCardMetaLabel}>Delay</span>
                                                        <span className={styles.callCardMetaVal}>{entry.delay_info}</span>
                                                    </div>
                                                )}
                                            </div>
                                            {entry.outcome && (
                                                <div className={styles.callCardAnalysis}>
                                                    <div className={styles.callCardAnalysisOutcome} style={{ color: stateColors[entry.outcome] || color }}>
                                                        {entry.outcome.toUpperCase()}
                                                    </div>
                                                    {entry.summary && <div className={styles.callCardAnalysisSummary}>{entry.summary}</div>}
                                                </div>
                                            )}
                                            {hasCollected && (
                                                <div className={styles.callCardDataGrid}>
                                                    {Object.entries(convoCollected!).map(([k, v], ki) => (
                                                        <div key={k} className={styles.callCardDataRow} style={{ animationDelay: `${0.05 + ki * 0.04}s` }}>
                                                            <span className={styles.callCardDataKey}>{k.replace(/_/g, ' ')}</span>
                                                            <span className={styles.callCardDataVal}>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    };

                    // ── single entry tooltip (for non-stacked) ──
                    const renderSingleTooltip = (entry: gcampaignscheduleentry) => {
                        const color = stateColors[entry.state] || '#666';
                        const convoCollected = entry.convo_id ? item.convo_data?.[String(entry.convo_id)] : undefined;
                        return (
                            <div className={styles.tlTooltip}>
                                <div className={styles.tlTooltipState}>
                                    <span className={styles.tlTooltipStateDot} style={{ background: color }} />
                                    <span style={{ color }}>{stateLabel(entry.state)}</span>
                                </div>
                                {entry.updated_at && (
                                    <div className={styles.tlTooltipRow}>
                                        <span className={styles.tlTooltipRowLabel}>Updated</span>
                                        <span className={styles.tlTooltipRowValue}>{formatTime(entry.updated_at)}</span>
                                    </div>
                                )}
                                {entry.scheduled_time && (
                                    <div className={styles.tlTooltipRow}>
                                        <span className={styles.tlTooltipRowLabel}>Scheduled</span>
                                        <span className={styles.tlTooltipRowValue}>{formatTime(entry.scheduled_time)}</span>
                                    </div>
                                )}
                                {entry.convo_id && (
                                    <div className={styles.tlTooltipRow}>
                                        <span className={styles.tlTooltipRowLabel}>Convo</span>
                                        <span className={styles.tlTooltipRowValue} style={{ fontFamily: 'monospace', fontSize: 10 }}>{entry.convo_id}</span>
                                    </div>
                                )}
                                {entry.outcome && (
                                    <>
                                        <div className={styles.tlTooltipDivider} />
                                        <div className={styles.tlTooltipRow}>
                                            <span className={styles.tlTooltipRowLabel}>Outcome</span>
                                            <span className={styles.tlTooltipRowValue} style={{ color: stateColors[entry.outcome] || color, fontWeight: 700 }}>{entry.outcome}</span>
                                        </div>
                                        {entry.summary && (
                                            <div className={styles.tlTooltipRow}>
                                                <span className={styles.tlTooltipRowLabel}>Summary</span>
                                                <span className={styles.tlTooltipRowValue}>{entry.summary}</span>
                                            </div>
                                        )}
                                    </>
                                )}
                                {convoCollected && Object.keys(convoCollected).length > 0 && (
                                    <>
                                        <div className={styles.tlTooltipDivider} />
                                        <span className={styles.tlTooltipConvoTitle}>Collected Data</span>
                                        <div className={styles.tlTooltipConvoGrid}>
                                            {Object.entries(convoCollected).map(([k, v]) => (
                                                <React.Fragment key={k}>
                                                    <span className={styles.tlTooltipConvoKey}>{k.replace(/_/g, ' ')}</span>
                                                    <span className={styles.tlTooltipConvoVal}>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    };

                    // ── campaign timeline view ──
                    if (selectedCampaignId) {
                        const rawSchedArr = item.schedules?.[selectedCampaignId];
                        if (!rawSchedArr || rawSchedArr.length === 0) return <span style={{ color: '#aaa', fontSize: 12 }}>—</span>;

                        // sort oldest → newest by updated_at (fallback to scheduled_time) so timeline reads left-to-right
                        const tsOf = (e: gcampaignscheduleentry) => {
                            const v = e.updated_at || e.scheduled_time;
                            const n = v ? Date.parse(v) : NaN;
                            return Number.isFinite(n) ? n : 0;
                        };
                        const schedArr = [...rawSchedArr].sort((a, b) => tsOf(a) - tsOf(b));

                        const stacked  = stackEvents(schedArr);

                        const scrollTimeline = (dir: 'left' | 'right', container: HTMLElement | null) => {
                            if (!container) return;
                            const dot = container.querySelector(`.${styles.timelineDot}`) as HTMLElement | null;
                            const distance = (dot ? dot.offsetWidth + 6 : 60) * (dir === 'right' ? 1 : -1);
                            const duration = 320;
                            const start = container.scrollLeft;
                            let t0: number | null = null;
                            const ease = (t: number) => 1 - Math.pow(1 - t, 3); // easeOutCubic
                            const tick = (now: number) => {
                                if (!t0) t0 = now;
                                const p = Math.min((now - t0) / duration, 1);
                                container.scrollLeft = start + distance * ease(p);
                                if (p < 1) requestAnimationFrame(tick);
                            };
                            requestAnimationFrame(tick);
                        };

                        let tlRef: HTMLDivElement | null = null;

                        return (
                            <div className={styles.timelineOuter}>
                                <div className={styles.timelineArrow} onClick={() => scrollTimeline('left', tlRef)} title="Scroll left">◀</div>
                                <div className={styles.timelineWrap} ref={el => { tlRef = el; }}>
                                    <div className={styles.timelineLine} />
                                    <div className={styles.timelineDots}>
                                        {stacked.map((node, ni) => {
                                            const color    = stateColors[node.state] || '#666';
                                            const isFilled = TERMINAL.has(node.state);
                                            const isStack  = node.entries.length > 1;
                                            const hasConvo = node.entries.some(e => !!e.convo_id);
                                            const dotLabel = formatTime(node.latest.updated_at || node.latest.scheduled_time) || stateLabel(node.state);

                                            // stacked or call-initiated → rich card
                                            if (isStack || hasConvo) {
                                                return (
                                                    <TooltipHost key={ni} tooltipProps={{ onRenderContent: () => renderStackedCard(node) }} directionalHint={DirectionalHint.bottomCenter} calloutProps={{ ...tooltipStyles, calloutMaxWidth: 420, gapSpace: 0, isBeakVisible: false }} hostClassName={styles.timelineTooltipHost} closeDelay={500}>
                                                        <div className={`${styles.timelineDot} ${styles.timelineDotCall} ${isStack ? styles.timelineDotStacked : ''}`} style={{ color }}>
                                                            {isStack && <span className={styles.stackBadge} style={{ background: color }}>{node.entries.length}</span>}
                                                            <div className={styles.timelineDotCircle} />
                                                            <span className={styles.timelineDotLabel}>{dotLabel}</span>
                                                        </div>
                                                    </TooltipHost>
                                                );
                                            }

                                            // single, no convo → simple tooltip
                                            const dotClass = `${styles.timelineDot}${isFilled ? ` ${styles.timelineDotFilled}` : ''}`;
                                            return (
                                                <TooltipHost key={ni} tooltipProps={{ onRenderContent: () => renderSingleTooltip(node.latest) }} directionalHint={DirectionalHint.bottomCenter} calloutProps={{ ...tooltipStyles, gapSpace: 0, isBeakVisible: false }} hostClassName={styles.timelineTooltipHost} closeDelay={500}>
                                                    <div className={dotClass} style={{ color }}>
                                                        <div className={styles.timelineDotCircle} />
                                                        <span className={styles.timelineDotLabel}>{dotLabel}</span>
                                                    </div>
                                                </TooltipHost>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className={styles.timelineArrow} onClick={() => scrollTimeline('right', tlRef)} title="Scroll right">▶</div>
                            </div>
                        );
                    }

                    // "All Contacts" view — show schedule badges for every campaign
                    const entries = Object.entries(item.schedules || {}).filter(([cid]) => cid !== 'root');

                    if (entries.length === 0) return <span style={{ color: '#aaa', fontSize: 12 }}>—</span>;
                    return (
                        <div className={styles.schedRow}>
                            {entries.map(([cid, schedArr]) => {
                                if (!schedArr || schedArr.length === 0) return null;
                                const latest = schedArr[schedArr.length - 1];
                                const color = stateColors[latest.state] || '#666';
                                const campName = campaignNameMap[cid] || cid;
                                return (
                                    <TooltipHost key={cid} tooltipProps={{ onRenderContent: () => renderSingleTooltip(latest) }} directionalHint={DirectionalHint.bottomCenter} calloutProps={{ ...tooltipStyles, gapSpace: 0, isBeakVisible: false }} closeDelay={500}>
                                        <span className={styles.schedBadge} style={{ borderColor: color, color }}>
                                            <span className={styles.schedCampName}>{campName}</span>
                                            {schedArr.length > 1 ? `(${schedArr.length}) ` : ''}
                                            {stateLabel(latest.state)}
                                        </span>
                                    </TooltipHost>
                                );
                            })}
                        </div>
                    );
                }
            }]
        ),
        { key: "actions", name: '', fieldName: '', minWidth: 50, maxWidth: 50, isResizable: false, targetWidthProportion: 1,
            onRender: (item: gcampaigntarget) => editingTargetId === item.target_id
                ? (
                    <div className={styles.editActions}>
                        <IconButton iconProps={{ iconName: "Accept" }} title="Save"   styles={{ root: { height: 30, width: 26, color: "#107c10" }, icon: { fontSize: 13 } }} onClick={saveEdit} />
                        <IconButton iconProps={{ iconName: "Cancel" }} title="Cancel" styles={{ root: { height: 30, width: 26 }, icon: { fontSize: 12 } }} onClick={cancelEdit} />
                    </div>
                )
                : (
                    <div className={styles.editActions}>
                        <IconButton iconProps={{ iconName: "Edit" }} title="Edit contact" styles={{ root: { height: 30, width: 26 }, icon: { fontSize: 12 } }} onClick={() => startEdit(item)} />
                        <IconButton iconProps={{ iconName: "Cancel" }} title={selectedCampaignId ? "Remove from campaign" : "Remove from first campaign"} styles={{ root: { height: 26, width: 26 }, icon: { fontSize: 12 } }} onClick={() => removeTarget(item)} />
                    </div>
                )
        },
    ];

    // ───── render ─────
    if (loading && campaigns.length === 0) return <Spinner size={SpinnerSize.medium} label="Loading campaigns…" />;

    import.meta.env.DEV && console.log("‹selectedCampaign›", selectedCampaignId);
    return (
        <div className={styles.container}>
                <div className={styles.editFormSectionTitle}>
                    <span>{"Campaign Configuration"}</span>
                </div>
            {/* top bar: campaign filter + buttons */}
            <div className={styles.topBar}>
                <Dropdown
                    placeholder="All Contacts"
                    options={campaignOptions}
                    selectedKey={selectedCampaignId}
                    onChange={(_, opt) => setSelectedCampaignId((opt?.key as string) ?? 'root')}
                    className={styles.campaignDropdown}
                    styles={cmDropdownStyles}
                />
                <PrimaryButton text="New Campaign" onClick={() => openNewModal(false)} styles={cmPrimaryButtonStyles} />
                {selectedCampaignId && (
                    <>
                        {/* delete & edit campaign buttons */}
                        {selectedCampaignId != 'root' && (
                            <>
                                <DefaultButton text="Edit" onClick={() => openNewModal(true)} styles={cmDefaultButtonStyles} />
                            <IconButton iconProps={{ iconName: "Delete" }} title="Delete campaign" onClick={deleteCampaign} styles={{ root: { borderRadius: "5px" } }} />
                                </>
                        )}


                        {/* schedule campaign & schedule test campaign buttons */}
                        {isChatCampaign ? (
                            <DefaultButton
                                text="Send Mail Invites"
                                title="Email all contacts in this campaign an invite to chat"
                                iconProps={{ iconName: "Mail" }}
                                styles={cmDefaultButtonStyles}
                                onClick={sendCampaignInvites}
                            />
                        ) : selectedCampaignId != 'root' ? (
                            <>
                                <DefaultButton
                                    text="Schedule Calls"
                                    title="Schedule outbound calls for all un-scheduled contacts in this campaign"
                                    styles={cmDefaultButtonStyles}
                                    onClick={async () => {
                                        try {
                                            const res = await qScheduleCampaign(
                                                loggedInUser,
                                                selectedCampaignId,
                                                selectedGroup.selectionId,
                                                selectedGroup.companyid || ''
                                            );
                                            toast.success(`Scheduled ${res.scheduled ?? 0} call(s). Skipped: ${res.skipped ?? 0}.`);
                                            await loadAllTargets();
                                            await loadCampaigns();
                                        } catch { toast.error('Failed to schedule campaign calls'); }
                                    }}
                                />
                                <DefaultButton
                                    text="Schedule Test Calls"
                                    title="Test mode: first call in 3 min, then +5 min for each subsequent contact"
                                    styles={{ ...cmDefaultButtonStyles, root: { ...(cmDefaultButtonStyles.root as object), borderColor: '#e67700', color: '#e67700' } }}
                                    onClick={async () => {
                                        try {
                                            const res = await qScheduleCampaign(
                                                loggedInUser,
                                                selectedCampaignId,
                                                selectedGroup.selectionId,
                                                selectedGroup.companyid || '',
                                                undefined,
                                                undefined,
                                                3,
                                                5,
                                            );
                                            toast.success(`Test scheduled ${res.scheduled ?? 0} call(s). First in ~3 min, +5 min each. Skipped: ${res.skipped ?? 0}.`);
                                            await loadAllTargets();
                                            await loadCampaigns();
                                        } catch { toast.error('Failed to schedule test calls'); }
                                    }}
                                />
                            </>
                        ) : null}
                    </>
                )}
            </div>

            {/* info card */}
            {selectedCampaign && (
                <div className={styles.infoCard}>
                    {selectedCampaign.campaign_config?.description && (
                        <div className={styles.infoStat}>
                            <span className={styles.infoStatLabel}>Description</span>
                            <span className={styles.infoStatValue}>{selectedCampaign.campaign_config.description}</span>
                        </div>
                    )}

                    {/* 3 stat cards */}
                    <div className={styles.statsRow}>
                        <div className={styles.statCard}>
                            <span className={styles.statNumber}>{selectedCampaign.total_targets}</span>
                            <span className={styles.statLabel}>Contacts</span>
                        </div>
                        <div className={styles.statCard}>
                            <span className={styles.statNumber}>{selectedCampaign.scheduled_count}</span>
                            <span className={styles.statLabel}>Scheduled</span>
                        </div>
                        <div className={styles.statCard}>
                            <span className={styles.statNumber}>{selectedCampaign.completed_count}</span>
                            <span className={styles.statLabel}>Completed</span>
                        </div>
                    </div>

                    {/* settings row */}
                    <div className={styles.settingsRow}>
                        {selectedCampaign?.campaign_start_date && (
                            <div className={styles.infoStat}>
                                <span className={styles.infoStatLabel}>Start</span>
                                <span className={styles.infoStatValue}>{toIsoDateStr(selectedCampaign.campaign_start_date)}</span>
                            </div>
                        )}
                        {selectedCampaign?.campaign_end_date && (
                            <div className={styles.infoStat}>
                                <span className={styles.infoStatLabel}>End</span>
                                <span className={styles.infoStatValue}>{toIsoDateStr(selectedCampaign.campaign_end_date)}</span>
                            </div>
                        )}

                        <DateRangePicker
                            className={styles.dateRangePicker}
                            label="Start / End Dates"
                            granularity="day"
                            value={getDateRange(selectedCampaign)}
                            onChange={(range) => {
                                import.meta.env.DEV && console.log("‹range›", range);
                                if (!selectedCampaign) return;
                                // save immediately on any picker change; if one boundary is missing, keep the currently persisted boundary.
                                const s = range?.start?.toString() || toIsoDateStr(selectedCampaign?.campaign_start_date) || '';
                                const e = range?.end?.toString()   || toIsoDateStr(selectedCampaign?.campaign_end_date)   || '';

                                if (!s && !e) return;

                                setCampaigns(prev => prev.map(c =>
                                    c.campaign_id === selectedCampaignId
                                        ? { ...c, campaign_start_date: s, campaign_end_date: e }
                                        : c
                                ));
                                qAddCampaign(
                                    loggedInUser,
                                    selectedGroup.selectionId,
                                    selectedCampaign.campaign_config,
                                    selectedCampaign.campaign_id,
                                    s,
                                    e,
                                    selectedCampaign.campaign_display_name
                                ).catch(() => toast.error('Failed to save campaign dates'));
                            }}
                        />
                        <TooltipHost content={<div className={styles.cellTooltipText} dangerouslySetInnerHTML={{ __html: `<div class=${styles.callTitle}>Enable/Disable Campaign</div><div class=${styles.cellText}>Enabled campaign</div> Starts & ends according to the specified dates.<div class=${styles.cellText}>Disabled campaign</div> Stops immediately and does not resume until re-enabled.` }}/>} directionalHint={DirectionalHint.bottomRightEdge} calloutProps={tooltipStyles}>
                            <Toggle
                                onText="Enabled"
                                offText="Disabled"
                                label={"Campaign Status"}
                                styles={toggleStylesNewCampaignEnable}
                                checked={isInactive ? false : true}
                                onChange={async (_, checked) => {
                                    if (!selectedCampaignId) return;
                                    setNewName(selectedCampaign.campaign_display_name || '');
                                    setNewDesc(selectedCampaign.campaign_config?.description || '');
                                    setNewStartDate(selectedCampaign.campaign_start_date || '');
                                    setNewEndDate(selectedCampaign.campaign_end_date || '');
                                    setIsInactive(checked ? 0 : 1);
                                    saveCampaignV2(
                                        true,
                                        selectedCampaign.campaign_display_name || '',
                                        selectedCampaign.campaign_config?.description || '',
                                        selectedCampaign.campaign_start_date || '',
                                        selectedCampaign.campaign_end_date || '',
                                        checked ? 0 : 1,
                                        selectedCampaignId
                                    );
                                    // clear default if disabling
                                    if (!checked && selectedGroup.def_campaign_id === selectedCampaignId) {
                                        try {
                                            await qSetDefCampaign(loggedInUser, selectedGroup.selectionId, '');
                                            onGroupUpdate?.({ def_campaign_id: '' });
                                        } catch {}
                                    }
                                }}
                            />
                        </TooltipHost>
                        <TooltipHost content={isInactive ? 'Inactive campaign cannot be default' : 'Set as the default campaign for this assistant'} directionalHint={DirectionalHint.bottomCenter} calloutProps={tooltipStyles} styles={{ root: { display: 'flex', alignSelf: 'end !important', margin:"0px !important", bottomMargin: "-10px"} }}>
                            <Checkbox
                                label="Default"
                                checked={selectedGroup.def_campaign_id === selectedCampaign.campaign_id}
                                disabled={isInactive === 1}
                                styles={{
                                    root: { marginLeft: 12, opacity: isInactive ? 0.4 : 1, transition: 'opacity 0.2s ease', display: 'block', alignSelf: "end"},
                                    checkbox: {
                                        borderRadius: 4,
                                        borderColor: selectedGroup.def_campaign_id === selectedCampaignId ? '#56778c' : '#b9daea',
                                        background:  selectedGroup.def_campaign_id === selectedCampaignId ? 'linear-gradient(135deg, #56778c, #6d99b3)' : 'transparent',
                                        transition: 'all 0.2s ease',
                                        selectors: { ':hover': { borderColor: '#56778c' } }
                                    },
                                    text: { fontFamily: 'Raleway, Urbanist, Roboto', fontSize: 12, fontWeight: 500, color: '#56778c' }
                                }}
                                onChange={async (_, checked) => {
                                    const newVal = checked ? selectedCampaignId : '';
                                    try {
                                        await qSetDefCampaign(loggedInUser, selectedGroup.selectionId, newVal);
                                        onGroupUpdate?.({ def_campaign_id: newVal });
                                    } catch { toast.error('Failed to set default campaign')}
                                }}
                            />
                        </TooltipHost>
                    </div>
                </div>
            )}

            {/* actions: csv upload */}
            <div className={styles.actions}>
                <TooltipHost content={'Bulk operations are available in the full version of Kognify CCO. Please contact us for more info.'} directionalHint={DirectionalHint.bottomCenter} calloutProps={tooltipStyles} styles={{ root: { display: 'flex', alignSelf: 'end !important', margin:"0px !important", bottomMargin: "-10px"} }}>
                    <DefaultButton text="Import CSV" onClick={() => csvRef.current?.click()} styles={cmDefaultButtonStyles} disabled={true} />
                </TooltipHost>
                <input ref={csvRef} type="file" accept=".csv" className={styles.hiddenInput} onChange={handleCSV} />
            </div>

            {/* phone members list OR chat members list */}
            {isChatCampaign && selectedCampaign ? (
                <CampaignChatMembers campaign={selectedCampaign} selectedGroup={selectedGroup} loggedInUser={loggedInUser} refreshKey={membersRefreshKey} />
            ) : loadingTargets ? (
                <Spinner size={SpinnerSize.medium} label="Loading contacts…" />
            ) : displayedTargets.length > 0 ? (
                /* phone campaign list */
                <div className={styles.tableWrap}>
                    <DetailsList
                        items={displayedTargets}
                        columns={columns as IColumn[]}

                        layoutMode={DetailsListLayoutMode.justified}
                        selectionMode={SelectionMode.none}
                        isHeaderVisible={true}
                        onRenderRow={(prps: IDetailsRowProps | undefined): JSX.Element => {
                            return <DetailsRow {...(prps as IDetailsRowProps)} styles={prps?.styles} className={styles.detailsRowCustom} />;
                        }}
                        onRenderDetailsHeader={(props?: IDetailsHeaderProps) => onRenderDetailsHeaderV2(props!)}
                        styles={detailsListCampaignStyles}
                    />
                </div>
            ) : (
                (selectedCampaignId != 'root') ? (
                    <div className={styles.emptyState}>
                        {selectedCampaignId ? "No contacts in this campaign. Add manually or import a CSV." : "No contacts yet. Add manually or import a CSV."}
                    </div>
                ) : (
                    <div className={styles.emptyState}>
                        {"Create a campaign to get started."}
                    </div>
                )
            )}

            {/* inline add row under table */}
            {(selectedCampaignId && selectedCampaignId != 'root') && (
                <div className={styles.addRowWrap}>
                    <div className={styles.addLabel}>
                        <span className={styles.addLabelIcon}>+</span>
                        <span className={styles.addLabelText}>Add</span>
                    </div>
                    <div className={styles.addRowSlider}>
                        <div className={styles.addRow}>
                            <TextField placeholder="Name" value={addName} onChange={(_, v) => setAddName(v || '')} className={styles.addField} styles={campaignTextFieldStyles} />
                            <TextField placeholder={isChatCampaign ? "Phone" : "Phone *"} value={addPhone} onChange={(_, v) => setAddPhone(v || '')} className={styles.addField} styles={campaignTextFieldStyles} />
                            <TextField placeholder={isChatCampaign ? "Email *" : "Email"} value={addEmail} onChange={(_, v) => setAddEmail(v || '')} className={styles.addField} styles={campaignTextFieldStyles} />
                            <TextField placeholder="Position" value={addPosition} onChange={(_, v) => setAddPosition(v || '')} className={styles.addField} styles={campaignTextFieldStyles} />
                            <PrimaryButton text="Add" onClick={() => addManualContact(selectedCampaignId || '')} disabled={isChatCampaign ? !addEmail.trim() : !addPhone.trim()} styles={cmSmallPrimaryStyles} />
                        </div>
                    </div>
                </div>
            )}
            {/* new / edit campaign modal */}
            <Modal styles={{ main: styles.modalNewCampaign }} isOpen={showNewModal} onDismiss={() => setShowNewModal(false)} isBlocking={false} >
                <div className={styles.modalContent}>
                    <h3 style={{ margin: 0 }}>{isEditing ? "Edit Campaign" : "New Campaign"}</h3>
                    <TextField label="Name" required value={newName} onChange={(_, v) => setNewName(v || '')} styles={campaignTextFieldStyles} />
                    <Dropdown
                        label="Type"
                        selectedKey={newType}
                        options={campaignTypeOptions}
                        disabled={isEditing}
                        onChange={(_, opt) => setNewType((opt?.key as 'phone' | 'chat') ?? 'phone')}
                        styles={cmDropdownStyles}
                    />
                    {isEditing && <span style={{ fontSize: 12, color: '#888' }}>Campaign type can't be changed after creation.</span>}
                    <TextField label="Description" multiline rows={2} value={newDesc} onChange={(_, v) => setNewDesc(v || '')} styles={campaignTextFieldStyles} />
                    <div className={styles.modalActions}>
                        <DefaultButton text="Cancel" onClick={() => setShowNewModal(false)} styles={cmDefaultButtonStyles} />
                        <PrimaryButton text={isEditing ? "Save" : "Create"} onClick={saveCampaign} styles={cmPrimaryButtonStyles} />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default CampaignManager;
